import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import QuickActionButton from '../../components/ui/QuickActionButton';

import Button from '../../components/ui/Button';
import OrderFilters from './components/OrderFilters';
import ServiceTypeCounts from './components/ServiceTypeCounts';
import OrderTable from './components/OrderTable';
import BulkActionsToolbar from './components/BulkActionsToolbar';
import OrderSummaryPanel from './components/OrderSummaryPanel';
import NewOrderModal from './components/NewOrderModal';

import { orderService } from '../../services/orderService';
import { vehicleService } from '../../services/vehicleService';
import { useAuth } from '../../contexts/AuthContext';

const OrderManagementDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showNewOrderModal, setShowNewOrderModal] = useState(false);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    dealership: '',
    technician: '',
    priority: '',
    dateFrom: '',
    dateTo: ''
  });

  // Load orders from database
  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Convert frontend filters to backend format
      const backendFilters = {};
      if (filters.search) backendFilters.search = filters.search;
      if (filters.status) backendFilters.status = filters.status;
      if (filters.priority) backendFilters.priority = filters.priority;
      if (filters.dealership) backendFilters.car_house_id = filters.dealership;
      if (filters.technician) {
        if (filters.technician === 'unassigned') {
          backendFilters.assigned_technician_id = null;
        } else {
          backendFilters.assigned_technician_id = filters.technician;
        }
      }
      if (filters.dateFrom) backendFilters.date_from = filters.dateFrom;
      if (filters.dateTo) backendFilters.date_to = filters.dateTo;

      const { data, error: fetchError } = await orderService.getAllOrders(backendFilters);
      
      if (fetchError) {
        throw fetchError;
      }

      setOrders(data || []);
    } catch (err) {
      console.error('Error loading orders:', err);
      setError(err.message || 'Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Load orders on component mount and when filters change
  useEffect(() => {
    loadOrders();
  }, [filters]);

  // Mock summary data based on loaded orders
  const summaryData = {
    newOrders: orders?.filter(o => o?.status === 'pending' || o?.status === 'review')?.length || 0,
    inProgress: orders?.filter(o => o?.status === 'in_progress')?.length || 0,
    completedToday: orders?.filter(o => o?.status === 'completed' && 
      new Date(o.updated_at)?.toDateString() === new Date()?.toDateString())?.length || 0,
    overdue: orders?.filter(o => {
      const createdDate = new Date(o.created_at);
      const daysDiff = (new Date() - createdDate) / (1000 * 60 * 60 * 24);
      return daysDiff > 3 && !['completed', 'cancelled']?.includes(o?.status);
    })?.length || 0
  };

  // Notification counts for header
  const notificationCounts = {
    orders: summaryData?.newOrders + summaryData?.overdue,
    technicians: 2
  };

  // Filter orders based on current filters (client-side filtering for additional features)
  const filteredOrders = orders?.filter(order => {
    if (filters?.search && !order?.vin?.toLowerCase()?.includes(filters?.search?.toLowerCase()) &&
        !order?.license_plate?.toLowerCase()?.includes(filters?.search?.toLowerCase()) &&
        !order?.order_number?.toLowerCase()?.includes(filters?.search?.toLowerCase())) {
      return false;
    }
    if (filters?.status && order?.status !== filters?.status) return false;
    if (filters?.dealership && order?.car_house_id !== filters?.dealership) return false;
    if (filters?.technician) {
      if (filters?.technician === 'unassigned' && order?.assigned_technician_id) return false;
      if (filters?.technician !== 'unassigned' && order?.assigned_technician_id !== filters?.technician) return false;
    }
    if (filters?.priority && order?.priority !== filters?.priority) return false;
    if (filters?.dateFrom) {
      const orderDate = new Date(order.created_at);
      const fromDate = new Date(filters.dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      if (orderDate < fromDate) return false;
    }
    if (filters?.dateTo) {
      const orderDate = new Date(order.created_at);
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (orderDate > toDate) return false;
    }
    return true;
  });

  const handleOrderSelect = (orderId, isSelected) => {
    if (isSelected) {
      setSelectedOrders(prev => [...prev, orderId]);
    } else {
      setSelectedOrders(prev => prev?.filter(id => id !== orderId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedOrders(filteredOrders?.map(order => order?.id));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      // Prepare update data
      const updateData = { status: newStatus };
      
      // If status is being changed to 'pending' (Neu) or 'review' (In Prüfung), unassign technician
      if (newStatus === 'pending' || newStatus === 'review') {
        updateData.assigned_technician_id = null;
      }
      
      const result = await orderService.updateOrder(orderId, updateData, user.id);
      
      if (result.error) {
        console.error('Error updating order status:', result.error);
        alert('Fehler beim Aktualisieren des Auftragsstatus: ' + result.error.message);
      } else {
        // Reload orders to reflect the change
        await loadOrders();
        
        // Show success message with technician unassignment info if applicable
        if (newStatus === 'pending' || newStatus === 'review') {
          alert(`Status erfolgreich zu "${newStatus === 'pending' ? 'Neu' : 'In Prüfung'}" geändert und Techniker abgemeldet!`);
        }
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Fehler beim Aktualisieren des Auftragsstatus: ' + error.message);
    }
  };

  const handleBulkAction = async (action, orderIds = selectedOrders) => {
    
    if (!user?.id) {
      alert('User not authenticated');
      return;
    }

    try {
      switch (action) {
        case 'assign':
          // Navigate to technician assignment
          navigate('/technician-assignment');
          break;
        case 'status-pending':
          // Update orders to pending status and unassign technicians
          for (const orderId of orderIds) {
            const result = await orderService.updateOrder(orderId, { 
              status: 'pending',
              assigned_technician_id: null 
            }, user.id);
            if (result.error) {
            }
          }
          await loadOrders(); // Reload orders
          alert(`Status erfolgreich zu "Neu" geändert und Techniker abgemeldet für ${orderIds.length} Auftrag${orderIds.length !== 1 ? 'e' : ''}`);
          break;
        case 'status-review':
          // Update orders to review status and unassign technicians
          for (const orderId of orderIds) {
            const result = await orderService.updateOrder(orderId, { 
              status: 'review',
              assigned_technician_id: null 
            }, user.id);
            if (result.error) {
              console.error('Error updating order:', result.error);
            }
          }
          await loadOrders(); // Reload orders
          alert(`Status erfolgreich zu "In Prüfung" geändert und Techniker abgemeldet für ${orderIds.length} Auftrag${orderIds.length !== 1 ? 'e' : ''}`);
          break;
        case 'status-assigned':
          // Update orders to assigned status - but only if they have a technician assigned
          let assignedCount = 0;
          let errorCount = 0;
          
          for (const orderId of orderIds) {
            const result = await orderService.updateOrder(orderId, { status: 'assigned' }, user.id);
            if (result.error) {
              errorCount++;
            } else {
              assignedCount++;
            }
          }
          
          await loadOrders(); // Reload orders
          
          if (errorCount > 0) {
            alert(`Status konnte nicht für ${errorCount} Auftrag${errorCount !== 1 ? 'e' : ''} geändert werden. Aufträge müssen zuerst einem Techniker zugewiesen werden.`);
          } else {
            alert(`Status erfolgreich zu "Zugewiesen" geändert für ${assignedCount} Auftrag${assignedCount !== 1 ? 'e' : ''}`);
          }
          break;
        case 'status-progress':
          // Update orders to in progress status - but only if they have a technician assigned
          let progressCount = 0;
          let progressErrorCount = 0;
          
          for (const orderId of orderIds) {
            const result = await orderService.updateOrder(orderId, { status: 'in_progress' }, user.id);
            if (result.error) {
              progressErrorCount++;
            } else {
              progressCount++;
            }
          }
          
          await loadOrders(); // Reload orders
          
          if (progressErrorCount > 0) {
            alert(`Status konnte nicht für ${progressErrorCount} Auftrag${progressErrorCount !== 1 ? 'e' : ''} geändert werden. Aufträge müssen zuerst einem Techniker zugewiesen werden.`);
          } else {
            alert(`Status erfolgreich zu "In Bearbeitung" geändert für ${progressCount} Auftrag${progressCount !== 1 ? 'e' : ''}`);
          }
          break;
        case 'status-completed':
          let completedCount = 0;
          let completedErrorCount = 0;
          
          for (const orderId of orderIds) {
            try {
              const result = await orderService.updateOrder(orderId, { status: 'completed' }, user.id);
              if (result.error) {
                completedErrorCount++;
              } else {
                completedCount++;
              }
            } catch (error) {
              completedErrorCount++;
            }
          }
          
          await loadOrders(); // Reload orders
          
          if (completedErrorCount > 0) {
            alert(`Status konnte nicht für ${completedErrorCount} Auftrag${completedErrorCount !== 1 ? 'e' : ''} zu "Abgeschlossen" geändert werden. Prüfen Sie die Konsole für Details.`);
          } else {
            alert(`Status erfolgreich zu "Abgeschlossen" geändert für ${completedCount} Auftrag${completedCount !== 1 ? 'e' : ''}`);
          }
          break;
        case 'status-cancelled':
          for (const orderId of orderIds) {
            const result = await orderService.updateOrder(orderId, { status: 'cancelled' }, user.id);
            if (result.error) {
            }
          }
          await loadOrders(); // Reload orders
          alert(`Status erfolgreich zu "Storniert" geändert für ${orderIds.length} Auftrag${orderIds.length !== 1 ? 'e' : ''}`);
          break;
        case 'priority-low':
          for (const orderId of orderIds) {
            const result = await orderService.updateOrder(orderId, { priority: 'low' }, user.id);
            if (result.error) {
            }
          }
          await loadOrders(); // Reload orders
          alert(`Priorität erfolgreich zu "Niedrig" geändert für ${orderIds.length} Auftrag${orderIds.length !== 1 ? 'e' : ''}`);
          break;
        case 'priority-normal':
          for (const orderId of orderIds) {
            const result = await orderService.updateOrder(orderId, { priority: 'normal' }, user.id);
            if (result.error) {
            }
          }
          await loadOrders(); // Reload orders
          alert(`Priorität erfolgreich zu "Normal" geändert für ${orderIds.length} Auftrag${orderIds.length !== 1 ? 'e' : ''}`);
          break;
        case 'priority-high':
          // Update orders to high priority
          for (const orderId of orderIds) {
            const result = await orderService.updateOrder(orderId, { priority: 'high' }, user.id);
            if (result.error) {
            }
          }
          await loadOrders(); // Reload orders
          alert(`Priorität erfolgreich zu "Hoch" geändert für ${orderIds.length} Auftrag${orderIds.length !== 1 ? 'e' : ''}`);
          break;
        case 'priority-urgent':
          // Update orders to urgent priority
          for (const orderId of orderIds) {
            const result = await orderService.updateOrder(orderId, { priority: 'urgent' }, user.id);
          }
          await loadOrders(); // Reload orders
          alert(`Priorität erfolgreich zu "Dringend" geändert für ${orderIds.length} Auftrag${orderIds.length !== 1 ? 'e' : ''}`);
          break;
        case 'delete':
          if (window.confirm(`${orderIds?.length} Auftrag${orderIds?.length !== 1 ? 'e' : ''} wirklich löschen?`)) {
            let deleteCount = 0;
            let errorCount = 0;
            
            for (const orderId of orderIds) {
              try {
                const result = await orderService.deleteOrder(orderId, user.id);
                if (result.error) {
                  console.error('Error deleting order:', result.error);
                  errorCount++;
                } else {
                  deleteCount++;
                }
              } catch (error) {
                console.error('Error deleting order:', error);
                errorCount++;
              }
            }
            
            await loadOrders(); // Reload orders
            
            if (errorCount > 0) {
              alert(`${deleteCount} Auftrag${deleteCount !== 1 ? 'e' : ''} erfolgreich gelöscht. ${errorCount} Auftrag${errorCount !== 1 ? 'e' : ''} konnten nicht gelöscht werden.`);
            } else {
              alert(`${deleteCount} Auftrag${deleteCount !== 1 ? 'e' : ''} erfolgreich gelöscht!`);
            }
          }
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error performing bulk action:', error);
      alert('Error performing bulk action: ' + error.message);
    }
    
    setSelectedOrders([]);
  };

  const handleExport = (format) => {
    // Mock export functionality
    const exportData = selectedOrders?.length > 0 
      ? orders?.filter(order => selectedOrders?.includes(order?.id))
      : filteredOrders;
    
    alert(`${exportData?.length} Aufträge werden als ${format?.toUpperCase()} exportiert.`);
  };

  const handleCreateOrder = async (orderData) => {
    try {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      console.log('Received orderData:', orderData);
      console.log('Make:', orderData.make);
      console.log('Model:', orderData.model);
      console.log('TotalAmount:', orderData.totalAmount);

      let vehicleId = null;

      // First, check if vehicle exists in database, if not create it
      if (orderData.vin) {
        try {
          const { data: existingVehicle } = await vehicleService.getVehicleByVIN(orderData.vin);
          if (existingVehicle) {
            vehicleId = existingVehicle.id;
          }
        } catch (vehicleError) {
          // Vehicle not found, will create new one
        }

        // If vehicle doesn't exist, create it
        if (!vehicleId) {
          // Helper function to validate and normalize enum values
          const normalizeFuelType = (fuelType) => {
            if (!fuelType || fuelType.toLowerCase() === 'n/a' || fuelType.toLowerCase() === 'unknown') {
              return 'gasoline'; // Default fallback
            }
            const normalized = fuelType.toLowerCase();
            const fuelTypeMap = {
              'petrol': 'gasoline',
              'diesel': 'diesel',
              'electric': 'electric',
              'hybrid': 'hybrid',
              'gasoline': 'gasoline',
              'gas': 'gasoline'
            };
            return fuelTypeMap[normalized] || 'gasoline';
          };

          const normalizeTransmission = (transmission) => {
            if (!transmission || transmission.toLowerCase() === 'n/a' || transmission.toLowerCase() === 'unknown') {
              return 'automatic'; // Default fallback
            }
            const normalized = transmission.toLowerCase();
            const transmissionMap = {
              'automatic': 'automatic',
              'manual': 'manual',
              'cvt': 'cvt',
              'semi-automatic': 'semi_automatic'
            };
            return transmissionMap[normalized] || 'automatic';
          };

          const vehiclePayload = {
            vin: orderData.vin,
            make: orderData.make || 'Unknown',
            model: orderData.model || 'Unknown',
            year: parseInt(orderData.year) || new Date().getFullYear(),
            engine_size: orderData.engine_size || null,
            color: orderData.color || null,
            fuel_type: orderData.fuel_type ? normalizeFuelType(orderData.fuel_type) : 'gasoline',
            transmission: orderData.transmission ? normalizeTransmission(orderData.transmission) : 'automatic',
            license_plate: orderData.licensePlate || null,
            mileage: orderData.mileage ? parseInt(orderData.mileage) : 0,
            car_house_id: orderData.car_house_id,
            additional_info: {
              created_via: 'manual_order',
              created_at: new Date().toISOString(),
              original_data: orderData
            }
          };

          const { data: newVehicle, error: vehicleCreateError } = await vehicleService.createVehicle(vehiclePayload);
          if (vehicleCreateError) {
            throw new Error(`Fehler beim Erstellen des Fahrzeugs: ${vehicleCreateError.message}`);
          }
          vehicleId = newVehicle.id;
        }
      }

      // Prepare order data with vehicle_id and correct field mapping
      const orderPayload = {
        title: orderData.title || `Auftrag für ${orderData.vin}`,
        description: orderData.description || 'Keine Beschreibung',
        status: 'pending',
        priority: orderData.priority || 'normal',
        customer_name: orderData.customerName || 'Unbekannt',
        customer_phone: orderData.customerPhone || '',
        customer_email: orderData.customerEmail || '',
        vin: orderData.vin,
        license_plate: orderData.licensePlate,
        mileage: orderData.mileage ? parseInt(orderData.mileage) : null,
        service_type_id: orderData.service_type_id || orderData.serviceType, // Handle both field names
        color: orderData.color,
        return_date: orderData.return_date || null,
        return_time: orderData.return_time || null, // This should be time without time zone
        car_house_id: orderData.car_house_id,
        vehicle_id: vehicleId,
        total_amount: orderData.totalAmount ? parseFloat(orderData.totalAmount) : null
      };

      console.log('Order payload before creation:', orderPayload);
      console.log('Total amount being sent:', orderPayload.total_amount);
      console.log('Service type ID being sent:', orderPayload.service_type_id);
      console.log('Car house ID being sent:', orderPayload.car_house_id);

      // Create order in database
      const { data: newOrder, error: createError } = await orderService.createOrder(orderPayload, user.id);
      
      if (createError) {
        throw createError;
      }

      // Reload orders to show the new order
      await loadOrders();
      
      alert('Auftrag und Fahrzeug erfolgreich erstellt!');
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Fehler beim Erstellen des Auftrags: ' + error.message);
    }
  };



  // Handle order deletion
  const handleDeleteOrder = async (order) => {
    if (!window.confirm(`Möchten Sie den Auftrag "${order.order_number}" wirklich löschen?`)) {
      return;
    }

    try {
      const { error } = await orderService.deleteOrder(order.id, user.id);
      
      if (error) {
        throw error;
      }

      // Refresh orders list
      await loadOrders();
      
      // Show success message
      alert('Auftrag erfolgreich gelöscht!');
    } catch (error) {
      alert('Fehler beim Löschen des Auftrags: ' + error.message);
    }
  };



  // Handle order editing
  const handleEditOrder = async (orderId, updatedData) => {
    // Refresh orders to show the updated data
    await loadOrders();
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '',
      dealership: '',
      technician: '',
      priority: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  const handleRefreshSummary = async () => {
    await loadOrders();
  };

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="admin" notificationCounts={notificationCounts} />
      {/* Hide sidebar on mobile */}
      <div className="hidden lg:block">
        <Sidebar 
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          userRole="admin"
          notificationCounts={notificationCounts}
        />
      </div>
      <main className={`transition-all duration-200 ease-out ${
        isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      } pt-16`}>
        <div className="flex">
          {/* Main Content */}
          <div className="flex-1 p-6 lg:pr-0">
            <div className="max-w-none">
              <Breadcrumb />
              
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-text-primary mb-2">Aufträge verwalten</h1>
                  <p className="text-text-secondary">
                    Übersicht und Verwaltung aller Service-Aufträge
                  </p>
                </div>
                
                <div className="flex items-center space-x-3 mt-4 lg:mt-0">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/vin-scanner')}
                    iconName="Scan"
                    iconPosition="left"
                  >
                    VIN Scanner
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => setShowNewOrderModal(true)}
                    iconName="Plus"
                    iconPosition="left"
                  >
                    Neuer Auftrag
                  </Button>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700">Error: {error}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadOrders}
                    className="mt-2"
                  >
                    Retry
                  </Button>
                </div>
              )}

              <OrderFilters
                filters={filters}
                onFiltersChange={setFilters}
                onClearFilters={handleClearFilters}
              />

              <ServiceTypeCounts
                orders={orders}
                loading={loading}
              />

              <BulkActionsToolbar
                selectedCount={selectedOrders?.length}
                onBulkAction={handleBulkAction}
                onExport={handleExport}
              />

              <OrderTable
                orders={filteredOrders}
                selectedOrders={selectedOrders}
                onOrderSelect={handleOrderSelect}
                onSelectAll={handleSelectAll}
                onBulkAction={handleBulkAction}
                onStatusUpdate={handleStatusUpdate}
                refreshTrigger={refreshTrigger}
                onEditOrder={handleEditOrder}
                onDeleteOrder={handleDeleteOrder}
                loading={loading}
              />
            </div>
          </div>

          {/* Right Sidebar - Summary Panel */}
          <div className="hidden xl:block w-10 ">
            {/* <OrderSummaryPanel
              summaryData={summaryData}
              onRefresh={handleRefreshSummary}
            /> */}
          </div>
        </div>
      </main>
      <QuickActionButton userRole="admin" />
      <NewOrderModal
        isOpen={showNewOrderModal}
        onClose={() => setShowNewOrderModal(false)}
        onCreateOrder={handleCreateOrder}
      />

    </div>
  );
};

export default OrderManagementDashboard;